import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Gift, ArrowLeft, Copy, Check, Plus, Users, MessageSquare, BarChart3,
  ChevronDown, Crown, Flame, Star, Send, Mail, DollarSign, UserCheck, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../services/api";
import { useAuth } from "../context/AuthContext";

const DURATIONS = [
  { label: "1 Week", days: 7 },
  { label: "1 Month", days: 30 },
  { label: "2 Months", days: 60 },
  { label: "3 Months", days: 90 },
  { label: "6 Months", days: 180 },
  { label: "1 Year", days: 365 },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function tierBadge(sub) {
  if (!sub) return { label: "Free", cls: "bg-slate-700 text-slate-300" };
  if (sub.source === "admin") return { label: "Owner", cls: "bg-amber-500/20 text-amber-300" };
  if (!sub.active) return { label: "Free", cls: "bg-slate-700 text-slate-300" };
  if (sub.source === "gift") return { label: "Gift", cls: "bg-pink-500/20 text-pink-300" };
  const t = (sub.tier || "solo") === "family" ? "Family" : "Solo";
  return { label: `${t} · Paid`, cls: "bg-green-500/20 text-green-300" };
}

/* ---------- Overview / Revenue ---------- */
function OverviewPanel({ stats }) {
  if (!stats) return <p className="text-slate-500 text-sm">Loading…</p>;
  const cards = [
    { testid: "stat-total-users", icon: Users, label: "Total sign-ups", value: stats.total_users, color: "text-indigo-300" },
    { testid: "stat-paying", icon: UserCheck, label: "Paying subscribers", value: stats.paying_users, color: "text-green-300" },
    { testid: "stat-gifted", icon: Gift, label: "On gift codes", value: stats.gifted_users, color: "text-pink-300" },
    { testid: "stat-free", icon: UserPlus, label: "Free users", value: stats.free_users, color: "text-slate-300" },
    { testid: "stat-mrr", icon: DollarSign, label: "Est. monthly revenue", value: `$${stats.estimated_mrr}`, color: "text-emerald-300" },
    { testid: "stat-profiles", icon: Star, label: "Child profiles", value: stats.total_profiles, color: "text-amber-300" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="bg-slate-900 rounded-2xl p-4 border border-slate-700">
            <c.icon className={`w-5 h-5 mb-2 ${c.color}`} />
            <div className="text-2xl font-black text-white">{c.value}</div>
            <div className="text-[11px] font-semibold text-slate-400">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-900 rounded-2xl p-4 border border-slate-700">
          <div className="text-xs font-bold text-slate-400 mb-1">Solo plans</div>
          <div className="text-xl font-black text-white">{stats.tier_counts?.solo || 0}</div>
        </div>
        <div className="bg-slate-900 rounded-2xl p-4 border border-slate-700">
          <div className="text-xs font-bold text-slate-400 mb-1">Family plans</div>
          <div className="text-xl font-black text-white">{stats.tier_counts?.family || 0}</div>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">
        Gift codes: {stats.gift_codes?.redeemed || 0} redeemed of {stats.gift_codes?.total || 0} created.
      </p>
    </div>
  );
}

/* ---------- Users ---------- */
function UsersPanel({ users }) {
  const [open, setOpen] = useState(null);
  if (!users) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (users.length === 0) return <p className="text-slate-500 text-sm">No sign-ups yet.</p>;
  return (
    <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
      {users.map((u) => {
        const badge = tierBadge(u.subscription);
        const isOpen = open === u.id;
        return (
          <div key={u.id} className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden" data-testid={`user-row-${u.id}`}>
            <button
              data-testid={`user-toggle-${u.id}`}
              onClick={() => setOpen(isOpen ? null : u.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-100 truncate">{u.name}</span>
                  {u.role === "admin" && <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                </div>
                <div className="text-xs text-slate-400 truncate">{u.email}</div>
              </div>
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${badge.cls}`}>{badge.label}</span>
              <span className="text-[10px] text-slate-500 hidden sm:block">{u.profile_count} kid{u.profile_count === 1 ? "" : "s"}</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-4 pb-4 pt-1 border-t border-slate-700/60 text-sm">
                <div className="grid grid-cols-2 gap-y-1 gap-x-4 text-xs mb-3">
                  <div className="text-slate-400">Joined</div><div className="text-slate-200 font-semibold">{fmtDate(u.created_at)}</div>
                  <div className="text-slate-400">Last active</div><div className="text-slate-200 font-semibold">{fmtDate(u.last_active)}</div>
                  <div className="text-slate-400">Plan</div><div className="text-slate-200 font-semibold">{u.subscription?.plan || "Free"}</div>
                  <div className="text-slate-400">Renews / ends</div><div className="text-slate-200 font-semibold">{fmtDate(u.subscription?.current_period_end)}</div>
                </div>
                {u.children?.length > 0 ? (
                  <div className="space-y-1">
                    {u.children.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 text-xs">
                        <span className="font-bold text-slate-100">{c.name}</span>
                        <span className="text-slate-400">· age {c.age_band}</span>
                        <span className="flex items-center gap-1 text-orange-400 ml-auto"><Flame className="w-3 h-3" />{c.streak}</span>
                        <span className="flex items-center gap-1 text-amber-400"><Star className="w-3 h-3" />{c.stars}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-slate-500 text-xs">No child profiles yet.</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Messages ---------- */
function MessagesPanel({ conversations, reload }) {
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const openThread = async (uid) => {
    setActive(uid);
    setThread(null);
    try {
      const { data } = await api.get(`/admin/conversations/${uid}`);
      setThread(data);
      reload();
    } catch (e) { toast.error("Could not load thread"); }
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/conversations/${active}/reply`, { body: reply.trim() });
      setReply("");
      const { data } = await api.get(`/admin/conversations/${active}`);
      setThread(data);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!conversations) return <p className="text-slate-500 text-sm">Loading…</p>;

  if (active && thread) {
    return (
      <div data-testid="owner-thread-view">
        <button data-testid="thread-back-btn" onClick={() => { setActive(null); setThread(null); }} className="flex items-center gap-1 text-slate-300 font-bold text-sm mb-3">
          <ArrowLeft className="w-4 h-4" /> All messages
        </button>
        <div className="mb-2">
          <div className="font-bold text-slate-100">{thread.user?.name}</div>
          <div className="text-xs text-slate-400">{thread.user?.email}</div>
        </div>
        <div className="space-y-2 max-h-[40vh] overflow-y-auto mb-3 bg-slate-900 rounded-xl p-3 border border-slate-700">
          {thread.messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "owner" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "owner" ? "bg-indigo-500 text-white" : "bg-slate-700 text-slate-100"}`}>
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[9px] mt-1 ${m.sender === "owner" ? "text-indigo-200" : "text-slate-400"}`}>{fmtDateTime(m.created_at)}</div>
              </div>
            </div>
          ))}
          {thread.messages.length === 0 && <p className="text-slate-500 text-sm">No messages.</p>}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="owner-reply-input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Write a reply…"
            className="flex-1 rounded-xl bg-slate-900 border border-slate-600 px-3 py-2.5 text-white outline-none focus:border-indigo-400"
          />
          <button data-testid="owner-reply-send" onClick={send} disabled={sending} className="pop-btn rounded-xl bg-indigo-500 text-white font-bold px-4 flex items-center gap-1 disabled:opacity-60">
            <Send className="w-4 h-4" /> {sending ? "…" : "Send"}
          </button>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) return <p className="text-slate-500 text-sm">No messages from parents yet.</p>;
  return (
    <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
      {conversations.map((c) => (
        <button
          key={c.user_id}
          data-testid={`convo-${c.user_id}`}
          onClick={() => openThread(c.user_id)}
          className="w-full text-left bg-slate-900 rounded-xl border border-slate-700 px-4 py-3 flex items-center gap-3 hover:border-indigo-500"
        >
          <Mail className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-100 truncate">{c.user_name}</span>
              {c.unread > 0 && <span className="text-[9px] font-black bg-red-500 text-white rounded-full px-1.5">{c.unread}</span>}
            </div>
            <div className="text-xs text-slate-400 truncate">{c.last_message}</div>
          </div>
          <span className="text-[10px] text-slate-500 shrink-0">{fmtDateTime(c.last_at)}</span>
        </button>
      ))}
    </div>
  );
}

/* ---------- Gift Codes ---------- */
function GiftCodesPanel({ codes, reload }) {
  const [duration, setDuration] = useState(30);
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState("1 Month");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/gift-codes", { duration_days: duration, count: Number(count), label });
      toast.success(`${count} gift code${count > 1 ? "s" : ""} created`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    toast.success("Copied!");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {DURATIONS.map((d) => (
          <button
            key={d.days}
            data-testid={`duration-${d.days}`}
            onClick={() => { setDuration(d.days); setLabel(d.label); }}
            className={`rounded-xl px-3 py-2 font-bold border-2 text-sm ${duration === d.days ? "bg-pink-500 border-pink-500 text-white" : "bg-slate-900 border-slate-600 text-slate-200"}`}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-5">
        <label className="text-sm font-semibold text-slate-400">How many?</label>
        <input data-testid="code-count-input" type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} className="w-20 rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-white font-bold" />
        <button data-testid="generate-codes-btn" onClick={generate} disabled={busy} className="pop-btn ml-auto flex items-center gap-2 rounded-xl bg-green-500 text-white font-bold px-5 py-2.5 shadow-[0_4px_0_#15803D] active:shadow-none disabled:opacity-60">
          <Plus className="w-4 h-4" /> {busy ? "…" : "Generate"}
        </button>
      </div>
      <h3 className="font-display font-bold text-slate-200 mb-2 text-sm">All codes ({codes?.length || 0})</h3>
      <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
        {(codes || []).map((c) => (
          <div key={c.code} className="flex items-center gap-3 bg-slate-900 rounded-xl px-4 py-3" data-testid={`giftcode-${c.code}`}>
            <span className="font-mono font-bold text-lg text-pink-300 tracking-wider">{c.code}</span>
            <span className="text-xs text-slate-400">{c.label || `${c.duration_days}d`}</span>
            {c.redeemed_by ? (
              <span className="ml-auto text-xs font-bold text-amber-400">Redeemed</span>
            ) : (
              <button onClick={() => copy(c.code)} className="ml-auto text-slate-300 hover:text-white">
                {copied === c.code ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              </button>
            )}
          </div>
        ))}
        {(!codes || codes.length === 0) && <p className="text-slate-500 text-sm">No codes yet.</p>}
      </div>
    </div>
  );
}

/* ---------- Dashboard shell ---------- */
export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [section, setSection] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [conversations, setConversations] = useState(null);
  const [codes, setCodes] = useState(null);
  const [unread, setUnread] = useState(0);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  }, []);
  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data.users); } catch {}
  }, []);
  const loadConvos = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/conversations"); setConversations(data.conversations);
      const u = await api.get("/admin/unread-count"); setUnread(u.data.unread);
    } catch {}
  }, []);
  const loadCodes = useCallback(async () => {
    try { const { data } = await api.get("/admin/gift-codes"); setCodes(data); } catch {}
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/profiles"); return; }
    if (user) { loadStats(); loadUsers(); loadConvos(); loadCodes(); }
  }, [user]); // eslint-disable-line

  const toggle = (s) => setSection(section === s ? null : s);

  if (user && user.role !== "admin") return null;

  const SECTIONS = [
    { key: "overview", label: "Overview & Revenue", icon: BarChart3, badge: null },
    { key: "users", label: "Users & Sign-ups", icon: Users, badge: users ? users.length : null },
    { key: "messages", label: "Messages", icon: MessageSquare, badge: unread || null },
    { key: "codes", label: "Gift Codes", icon: Gift, badge: null },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-5">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="admin-back-btn" onClick={() => navigate("/parent")} className="flex items-center gap-2 text-slate-300 font-bold">
            <ArrowLeft className="w-5 h-5" /> Portal
          </button>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-400" /> Owner Dashboard
          </h1>
          <span className="text-xs text-slate-500 truncate max-w-[120px]">{user?.email}</span>
        </div>

        <div className="space-y-3">
          {SECTIONS.map((s) => {
            const isOpen = section === s.key;
            return (
              <div key={s.key} className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <button
                  data-testid={`section-${s.key}`}
                  onClick={() => toggle(s.key)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left"
                >
                  <s.icon className="w-5 h-5 text-indigo-300" />
                  <span className="font-display font-bold flex-1">{s.label}</span>
                  {s.badge != null && s.badge !== 0 && (
                    <span className={`text-[10px] font-black rounded-full px-2 py-0.5 ${s.key === "messages" ? "bg-red-500 text-white" : "bg-slate-700 text-slate-300"}`}>{s.badge}</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-700/60">
                    {s.key === "overview" && <OverviewPanel stats={stats} />}
                    {s.key === "users" && <UsersPanel users={users} />}
                    {s.key === "messages" && <MessagesPanel conversations={conversations} reload={loadConvos} />}
                    {s.key === "codes" && <GiftCodesPanel codes={codes} reload={loadCodes} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
